/**
 * <product-recommendations> - loads Shopify product recommendations into its section.
 *
 * Uses Shopify's documented Product Recommendations endpoint: routes.product_recommendations_url with section_id,
 * product_id, limit and intent (related | complementary). Shopify renders this same section server-side with the
 * `recommendations` object populated and returns the HTML; this element swaps the result in. The cards are the
 * normal Product Card, and its custom elements upgrade on insertion like anywhere else.
 *
 *   - One request per element, and only when the section is about to scroll into view.
 *   - The request is aborted if the element leaves the page first (Theme Editor re-render, navigation).
 *   - No recommendations: on the storefront the whole section is hidden, so there is no empty band of section
 *     spacing; in the Theme Editor a short explanation is shown instead so the section stays selectable.
 *   - The inserted HTML is Shopify's own section render from the same origin. Browsers never execute <script>
 *     elements inserted through innerHTML, and the only scripts in it are the Product Card's JSON data islands.
 */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.onIntersect = this.onIntersect.bind(this);
    this.observer = null;
    this.controller = null;
    this.requested = false;
  }

  connectedCallback() {
    if (this.requested || !this.dataset.url) return;

    if (typeof IntersectionObserver === 'undefined') {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.controller?.abort();
    this.controller = null;
  }

  onIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.observer?.disconnect();
    this.observer = null;
    this.load();
  }

  async load() {
    if (this.requested) return;
    this.requested = true;
    this.controller = new AbortController();

    try {
      const response = await fetch(this.dataset.url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const results = html.querySelector('[data-recommendations-results]');
      const count = results ? parseInt(results.dataset.count, 10) || 0 : 0;

      if (count > 0) {
        this.innerHTML = results.outerHTML;
      } else {
        this.showEmpty();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Product recommendations failed to load', error);
      this.showEmpty();
    } finally {
      this.controller = null;
    }
  }

  showEmpty() {
    if (window.Shopify?.designMode && this.dataset.emptyMessage) {
      const wrapper = document.createElement('div');
      wrapper.className = 'section';
      const note = document.createElement('p');
      note.className = 'section-empty';
      note.textContent = this.dataset.emptyMessage;
      wrapper.append(note);
      this.replaceChildren(wrapper);
      return;
    }

    this.closest('.shopify-section')?.setAttribute('hidden', '');
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
